import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import type { SelectChangeEvent } from '@mui/material';
import {
  Alert,
  alpha,
  Box,
  Button,
  Fade,
  FormControl,
  Grid,
  InputLabel,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import CustomTextMask from '@/components/ui/CustomTextMask';
import Loader from '@/components/ui/Loader';
import type { ContactUsType } from '@/types';
import { PartnerType } from '@/types/enumTypes';

const SplitDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(12px)',
    backgroundColor: alpha('#0a1027', 0.65),
  },
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    maxWidth: '1000px',
    width: '100%',
    margin: theme.spacing(2),
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    boxShadow: '0 30px 60px -15px rgba(16, 27, 69, 0.35)',
    border: `1px solid ${alpha('#101b45', 0.08)}`,
  },
  '& .MuiDialogContent-root': {
    padding: 0,
  },
}));

export interface AffiliateContactPopupProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onSuccess?: () => void;
}

const defaultValues: ContactUsType = {
  Name: '',
  Email: '',
  Phone: '',
  Message: '',
  DesignationOfContactPerson: '',
  NatureOfBusiness: '',
  PartnerName: '',
  PartnerType: PartnerType.None,
  Website: '',
  Token: '',
  ExcelTemplate: null,
};

const AffiliateContactPopup = ({
  open,
  setOpen,
  onSuccess,
}: AffiliateContactPopupProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [openSnack, setSnackOpen] = useState(false);
  const [isLabelName, setIsLabelName] = useState(
    'Dynamic value based on answer for pervious question'
  );

  const validationSchema = Yup.object().shape(
    {
      Name: Yup.string().required('Name is required'),
      DesignationOfContactPerson: Yup.string().required(
        'Designation of contact person is required'
      ),
      Email: Yup.string()
        .required('Email Address is required')
        .email('Enter valid Email Address'),
      Phone: Yup.string()
        .required('Phone Number is required')
        .matches(
          /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
          'Please enter a valid phone number'
        ),
      Message: Yup.string().required('Description/Comments is required'),
    },
    [['Phone', 'Phone']]
  );

  const { handleSubmit, control, setValue, reset } = useForm<ContactUsType>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as unknown as Resolver<
      ContactUsType,
      any
    >,
    defaultValues,
  });

  const handleClose = () => {
    setOpen(false);
  };

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  const getDynamicLabel = (type: string | undefined | null) => {
    switch (type) {
      case PartnerType.Business:
        return 'Business name';
      case PartnerType.Influencer:
        return 'Social media account link for reference';
      case PartnerType.Association:
        return 'Association name';
      case PartnerType.TaxProfessional:
      case 'TaxProfessional':
        return 'Freelancer or entity';
      case PartnerType.SoftwareProvider:
      case 'SoftwareProvider':
        return 'Product Name';
      default:
        return 'Dynamic value based on answer for pervious question';
    }
  };

  const handlePartnerTypeChange = (
    event:
      | SelectChangeEvent<any>
      | ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>
      | { target: { value: string } }
  ) => {
    const selected = event.target.value;
    setValue('PartnerName', '');
    setIsLabelName(getDynamicLabel(selected));
  };

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setIsLabelName('Dynamic value based on answer for pervious question');
    }
  }, [open, reset]);

  useEffect(() => {
    const loadScriptByURL = (
      id: string,
      url: string,
      callback?: () => void
    ) => {
      const isScriptExist = document.getElementById(id);
      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        script.onload = () => {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }
      if (isScriptExist && callback) callback();
    };

    if (process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY) {
      loadScriptByURL(
        'recaptcha-key',
        `https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}&badge=bottomleft`
      );
    }
  }, []);

  const sendContactData = async (postData: ContactUsType) => {
    try {
      const response = await fetch('/api/contactus/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(postData),
      });
      const result = await response.json();
      if (result?.status === 'Success' || response.ok) {
        reset(defaultValues);
        setOpen(false);
        if (onSuccess) {
          onSuccess();
        } else {
          setSnackOpen(true);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to submit contact form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: ContactUsType) => {
    setIsLoading(true);
    try {
      if (
        typeof window !== 'undefined' &&
        (window as any).grecaptcha &&
        process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY
      ) {
        (window as any).grecaptcha.ready(() => {
          (window as any).grecaptcha
            .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
              action: 'submit',
            })
            .then(async (token: string) => {
              const postData: ContactUsType = { ...data, Token: token };
              await sendContactData(postData);
            })
            .catch(async () => {
              await sendContactData(data);
            });
        });
      } else {
        await sendContactData(data);
      }
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Loader isLoading={isLoading} />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openSnack}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        TransitionComponent={Fade}
        sx={{ top: '120px !important', zIndex: 99999 }}
      >
        <Alert
          onClose={handleSnackClose}
          variant="filled"
          severity="success"
          sx={{
            width: '100%',
            color: '#fff',
            fontWeight: 600,
          }}
        >
          Thanks for contacting us! We will be in touch with you shortly.
        </Alert>
      </Snackbar>

      <SplitDialog
        onClose={handleClose}
        aria-labelledby="affiliate-contact-popup-title"
        open={open}
        fullWidth
        maxWidth="lg"
      >
        <DialogContent>
          <Grid container sx={{ minHeight: { md: '640px' } }}>
            {/* Left Aesthetic Sidebar */}
            <Grid
              size={{ xs: 12, md: 4.2 }}
              sx={{
                background: 'linear-gradient(160deg, #101b45 0%, #0a112c 100%)',
                color: '#fff',
                p: { xs: 3.5, sm: 4.5 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
              }}
            >
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: alpha('#fff', 0.6),
                    fontWeight: 700,
                    letterSpacing: 2,
                  }}
                >
                  PARTNERSHIP
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: 24, sm: 35 },
                    fontWeight: 800,
                    mt: 1,
                    mb: 4,
                    color: '#ffffff',
                    lineHeight: 1.2,
                  }}
                >
                  Let’s Build Something Together
                </Typography>
                {/* <Typography
                  variant="body2"
                  sx={{
                    color: alpha('#fff', 0.75),
                    lineHeight: 1.6,
                    fontSize: 14,
                    mb: 4,
                  }}
                >
                  Fill out the form below to get in touch with our partner
                  onboarding team.
                </Typography> */}

                {/* Key value indicators */}
                <Stack spacing={2.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: '10px',
                        backgroundColor: alpha('#fff', 0.08),
                        display: 'flex',
                      }}
                    >
                      <DescriptionOutlinedIcon
                        sx={{ color: '#fff', fontSize: 22 }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        One platform, 60+ forms
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: alpha('#fff', 0.6) }}
                      >
                        Payroll to business taxes, all in one place
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: '10px',
                        backgroundColor: alpha('#fff', 0.08),
                        display: 'flex',
                      }}
                    >
                      <VerifiedUserOutlinedIcon
                        sx={{ color: '#fff', fontSize: 22 }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Build trust, grow reach
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: alpha('#fff', 0.6) }}
                      >
                        Recommend an IRS-authorized platform your audience
                        trusts
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: '10px',
                        backgroundColor: alpha('#fff', 0.08),
                        display: 'flex',
                      }}
                    >
                      <PaidOutlinedIcon sx={{ color: '#fff', fontSize: 22 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Earn 20% on every filing
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: alpha('#fff', 0.6) }}
                      >
                        Get paid for every successful referral
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: alpha('#fff', 0.4),
                  mt: 4,
                  display: { xs: 'none', md: 'block' },
                }}
              >
                Confidential & Secure Form
              </Typography>
            </Grid>

            {/* Right Form Area */}
            <Grid
              size={{ xs: 12, md: 7.8 }}
              sx={{
                p: { xs: 3, sm: 4.5 },
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Tooltip title="Close" arrow>
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: 16,
                    color: '#64748b',
                    backgroundColor: '#f1f5f9',
                    '&:hover': {
                      backgroundColor: '#e2e8f0',
                      color: '#101b45',
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h2"
                  id="affiliate-contact-popup-title"
                  sx={{
                    fontSize: { xs: 22, sm: 26 },
                    fontWeight: 700,
                    color: '#101b45',
                  }}
                >
                  Contact Us
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', mt: 0.5 }}
                >
                  Fill out the form below to get in touch with our partner
                  onboarding team.
                </Typography>
              </Box>

              <Box
                component="form"
                autoComplete="off"
                onSubmit={handleSubmit(onSubmit)}
                sx={{ width: '100%' }}
              >
                <Grid container spacing={2.5}>
                  {/* Row 1 */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="Name"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="Name"
                          label="*Name"
                          className={value ? 'prefilled' : 'mandatory'}
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="DesignationOfContactPerson"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="DesignationOfContactPerson"
                          label="*Designation of contact person"
                          className={value ? 'prefilled' : 'mandatory'}
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  {/* Row 2 */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel color="secondary">Who are you?</InputLabel>
                      <Controller
                        name="PartnerType"
                        control={control}
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <Select
                            native
                            labelId="PartnerType"
                            name="PartnerType"
                            id="PartnerType"
                            label="Who are you?"
                            color="secondary"
                            error={!!error}
                            className={
                              value && value !== PartnerType.None
                                ? 'prefilled'
                                : ''
                            }
                            fullWidth
                            value={value?.toString() || PartnerType.None}
                            onChange={(event) => {
                              onChange(event);
                              handlePartnerTypeChange(event);
                            }}
                          >
                            <option
                              key={PartnerType.None}
                              value={PartnerType.None}
                            >
                              Choose an option
                            </option>
                            <option
                              key={PartnerType.Business}
                              value={PartnerType.Business}
                            >
                              {PartnerType.Business}
                            </option>
                            <option
                              key={PartnerType.TaxProfessional}
                              value="TaxProfessional"
                            >
                              {PartnerType.TaxProfessional}
                            </option>
                            <option
                              key={PartnerType.Influencer}
                              value={PartnerType.Influencer}
                            >
                              {PartnerType.Influencer}
                            </option>
                            <option
                              key={PartnerType.Association}
                              value={PartnerType.Association}
                            >
                              {PartnerType.Association}
                            </option>
                            <option
                              key={PartnerType.SoftwareProvider}
                              value="SoftwareProvider"
                            >
                              {PartnerType.SoftwareProvider}
                            </option>
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="Phone"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="Phone"
                          label="*Phone Number"
                          className={value ? 'prefilled' : 'mandatory'}
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          InputProps={{
                            inputComponent: CustomTextMask as any,
                            inputProps: {
                              mask: '(#00) 000-0000',
                              maxLength: 14,
                            },
                          }}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  {/* Row 3 */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="PartnerName"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="PartnerName"
                          label={isLabelName}
                          className={value ? 'prefilled' : ''}
                          variant="outlined"
                          color="secondary"
                          value={value || ''}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          inputProps={{
                            maxLength: 150,
                          }}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="Email"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="Email"
                          label="*E-Mail address"
                          className={value ? 'prefilled' : 'mandatory'}
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          type="email"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  {/* Row 4 */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="Website"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="Website"
                          label="Website (if applicable):"
                          className={value ? 'prefilled' : ''}
                          variant="outlined"
                          color="secondary"
                          value={value || ''}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="NatureOfBusiness"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="NatureOfBusiness"
                          label="Nature of Business:"
                          className={value ? 'prefilled' : ''}
                          variant="outlined"
                          color="secondary"
                          value={value || ''}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  {/* Row 5 */}
                  <Grid size={12}>
                    <Controller
                      name="Message"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="Message"
                          label="*Description/Comments:"
                          className={value ? 'prefilled' : 'mandatory'}
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          multiline
                          rows={3}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  {/* Row 6: Submit Button */}
                  <Grid size={12} sx={{ textAlign: 'center', mt: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      className="btn-primary"
                      sx={{
                        minWidth: 220,
                        py: 1.4,
                        px: 5,
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        borderRadius: '8px',
                        transition: 'all 0.3s ease 0s',
                        '&:hover, &:focus, &:active': {
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </SplitDialog>
    </>
  );
};

export default AffiliateContactPopup;
